import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, type HTMLMotionProps } from "motion/react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { Image, type ImageProps } from "./Image";
import { Skeleton } from "./Skeleton";
import { PixelCard, type PixelCardProps } from "./PixelCard";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * Card — slot-composed surface for entity cards (Studios, Experiences, Collections,
 * etc.). Two layout modes via `view`: `"grid"` is the blog-7-style image-on-top
 * card; `"list"` is the blog-3-style horizontal row. Hover behaviour is opt-in
 * via `hover` ("lift" | "glow" | "none"); both variants honour
 * `prefers-reduced-motion` automatically.
 *
 * Composition is sub-component based, like shadcn:
 *
 *   <Card view="grid" hover="lift" onClick={...}>
 *     <CardCover src={...} aspectRatio="16/11" />
 *     <CardBody>
 *       <CardTag tone="violet">Quest</CardTag>
 *       <CardTitle>Build your first AI Studio</CardTitle>
 *       <CardDescription>Walk through topic, content, AI experience setup.</CardDescription>
 *       <CardMeta items={["Aug 6, 2025", "Sahaj Garg", "2 min read"]} />
 *       <CardStats stats={[{ icon: <Users />, value: 42 }]} />
 *     </CardBody>
 *     <CardFooter>
 *       <CardActions>...</CardActions>
 *     </CardFooter>
 *   </Card>
 *
 * For loading state, render `<CardSkeleton view="grid" />` directly — domain
 * shells (StudioCard, ExperienceCard, CollectionCard) export `.Skeleton` static
 * fields tuned to their slot tree.
 *
 * The cover image self-manages its loading + error chrome via the Image
 * primitive — consumers don't need to track imageLoading state per card.
 *
 * The Card family now wears the Onyx hex-notch silhouette (`nx-s`, the card
 * scale) instead of the old rounded-rect. The two details a `clip-path` would
 * break in the v1 way are handled per the surface recipe (issue #158):
 *   - ELEVATION is a clip-safe `.elev-card` `drop-shadow` (lifting to
 *     `.elev-panel` on hover-lift), NOT an outer `box-shadow` — a box-shadow is
 *     clipped away by the notch clip-path.
 *   - The OUTLINE is a continuous `nx-rim` RING overlay (the `[border]`-toned
 *     hairline that traces the full notched silhouette including the chamfer),
 *     NOT a real CSS `border` — a border is sliced at the notch diagonal.
 */

export const cardVariants = cva(
  // Base — notched surface, focus ring, transition. Sits as a generous
  // container with a clip-safe drop-shadow that lifts a notch on hover.
  // The "calm and relaxed" platform feel comes from breathing-room padding
  // inside the title section + a 16/9 prominent image, not from heavy chrome.
  // `nx-s` clips the hex-notch silhouette; `overflow-hidden` keeps the cover
  // image's hover scale inside that silhouette.
  // Layout + focus + transition only. The SURFACE chrome — the Onyx hex-notch
  // (clickable cards) OR the rounded-rect (static/display cards) — is applied
  // conditionally in the body, keyed off `interactive`: the notch is an
  // affordance for clickable surfaces, so a plain display card stays round.
  [
    "group/card relative bg-card text-card-foreground overflow-hidden",
    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "transition-[filter,box-shadow,transform,border-color] duration-200 ease-smooth-out",
  ].join(" "),
  {
    variants: {
      view: {
        grid: "flex flex-col",
        list: "flex flex-row items-stretch p-3 sm:p-4 gap-4",
      },
      // `hover` selects the lift/glow behaviour; the actual classes are applied
      // per surface kind (notched vs rounded) in the body.
      hover: {
        none: "",
        lift: "",
        glow: "",
      },
      interactive: {
        true: "cursor-pointer",
        false: "",
      },
    },
    defaultVariants: {
      view: "grid",
      hover: "lift",
      interactive: false,
    },
  },
);

/** Card notch scale — the `nx-s` (card) silhouette + its 1px rim ring clip. */
const CARD_NOTCH = nx.s;
const CARD_RING_CLIP = NX_RING_CLIP.s;

type CardBaseProps = VariantProps<typeof cardVariants> & {
  asChild?: boolean;
  /**
   * Disables Framer-Motion entrance + hover animation. Use for static / SSR
   * islands where motion would be a re-render cost without payoff. Reduced
   * motion is honoured automatically — no need to set this manually for a11y.
   */
  motionEnabled?: boolean;
  /**
   * Optional click handler. When set, the root gets `cursor-pointer`,
   * `role="button"`, keyboard activation, and a focus ring. The wrapping
   * element stays a `<div>` (not a `<button>`) so action buttons inside the
   * footer can still own their own click semantics.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export interface CardProps
  extends
    Omit<HTMLMotionProps<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag">,
    CardBaseProps {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      view,
      hover,
      interactive,
      asChild = false,
      motionEnabled = true,
      onClick,
      onKeyDown,
      tabIndex,
      role,
      children,
      ...props
    },
    ref,
  ) => {
    const reducedMotion = useReducedMotion();
    const isInteractive = interactive ?? Boolean(onClick);

    // Keyboard activation — Enter / Space trigger onClick when the card is interactive.
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || !onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          // Synthesise a mouse-event-shaped object for the click handler.
          (onClick as unknown as (e: React.SyntheticEvent) => void)(event);
        }
      },
      [onClick, onKeyDown],
    );

    // Clickable cards wear the Onyx hex-notch + clip-safe `.elev-*` + the
    // keyline ring; static/display cards keep the original rounded-rect chrome
    // (border + soft box-shadow). Owner direction: reserve the notch for
    // clickable surfaces, keep display items simple + round.
    const h = hover ?? "lift";
    const surface = isInteractive
      ? cn("elev-card", CARD_NOTCH, h !== "none" && "hover:elev-panel")
      : cn(
          "rounded-2xl border border-border/70",
          "shadow-[0_1px_2px_-1px_rgb(0_0_0_/_0.04),_0_4px_16px_-8px_rgb(0_0_0_/_0.06)]",
          h === "lift" && "hover:shadow-[0_8px_30px_-12px_rgb(0_0_0_/_0.12)] hover:border-border",
          h === "glow" &&
            "hover:shadow-[0_0_0_1px_hsl(var(--primary)/0.3),_0_8px_30px_-8px_hsl(var(--primary)/0.18)] hover:border-primary/40",
        );

    const sharedProps = {
      ref,
      className: cn(cardVariants({ view, hover, interactive: isInteractive }), surface, className),
      onClick,
      onKeyDown: isInteractive ? handleKeyDown : onKeyDown,
      tabIndex: isInteractive ? (tabIndex ?? 0) : tabIndex,
      role: isInteractive ? (role ?? "button") : role,
    };

    // Continuous keyline — a 1px `nx-rim` ring clipped to the notched
    // silhouette, NOT a CSS border (which the notch clip-path would slice at
    // the chamfer). Sits ABOVE the (clipped) cover image, below content. The
    // `glow` variant recolors the rim to the primary tint on group-hover.
    // The notched keyline ring — only the clickable (notched) card carries it;
    // the rounded static card uses its real border instead.
    const keyline = isInteractive ? (
      <span
        aria-hidden
        className={cn(
          "nx-rim pointer-events-none absolute inset-0 z-[1] [--nx-rim:hsl(var(--border))]",
          hover === "glow" && "group-hover/card:[--nx-rim:hsl(var(--primary)/0.5)]",
        )}
        style={{ clipPath: CARD_RING_CLIP }}
      />
    ) : null;

    if (asChild) {
      return (
        <Slot {...sharedProps} {...(props as React.HTMLAttributes<HTMLDivElement>)}>
          <Slottable>{children as React.ReactNode}</Slottable>
          {keyline}
        </Slot>
      );
    }

    if (!motionEnabled || reducedMotion) {
      return (
        <div {...sharedProps} {...(props as React.HTMLAttributes<HTMLDivElement>)}>
          {children as React.ReactNode}
          {keyline}
        </div>
      );
    }

    return (
      <motion.div
        {...sharedProps}
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
        // Note: card itself doesn't bounce on hover anymore — feedback is the
        // drop-shadow lift + cover-image scale, both purely visual (no layout
        // shift, no parent translate). The cover image scales `[1.03]`
        // inside `overflow-hidden` so it stays inside the notched card.
        {...props}
      >
        {children as React.ReactNode}
        {keyline}
      </motion.div>
    );
  },
);
Card.displayName = "Card";

/* -------------------------------------------------------------------------- */
/* CardCover — image slot with internal load state + radius inheritance       */
/* -------------------------------------------------------------------------- */

const cardCoverVariants = cva("relative overflow-hidden bg-muted", {
  variants: {
    view: {
      // In grid view the cover sits flush against the top of the card
      // (community-card pattern). The parent Card clips the hex-notch
      // silhouette (`nx-s` + `overflow-hidden`), so the cover's top edge —
      // including the top-right notch chamfer — is shaped by that clip; the
      // cover carries no radius of its own.
      grid: "w-full",
      // List covers are an inner thumbnail (not flush to the notch edge), so
      // they keep a small independent radius.
      list: "rounded-lg flex-shrink-0 w-32 sm:w-40 lg:w-48",
    },
    aspect: {
      "16/11": "aspect-[16/11]",
      "16/9": "aspect-video",
      "4/3": "aspect-[4/3]",
      square: "aspect-square",
    },
  },
  defaultVariants: {
    view: "grid",
    aspect: "16/9",
  },
});

/**
 * Pixel-effect config passed to `<CardCover pixelEffect={...} />`. Accepts any
 * subset of the standalone `<PixelCard>` props (variant, gap, speed, colours,
 * focus behaviour). When `pixelEffect` is just `true`, defaults to the
 * `"muted"` variant which is tuned for overlaying photographic covers.
 */
export type CardCoverPixelEffect =
  | boolean
  | Pick<PixelCardProps, "variant" | "gap" | "speed" | "colors" | "noFocus">;

export interface CardCoverProps
  extends
    Omit<ImageProps, "className" | "aspectRatio" | "ref" | "errorContent">,
    VariantProps<typeof cardCoverVariants> {
  className?: string;
  /**
   * Children render absolutely-positioned over the image (badges, overlay
   * controls, gradient veil). Wrapper is `position: relative` so any absolute
   * children compose without layout work in the consumer.
   */
  children?: React.ReactNode;
  /**
   * Hover scale of the inner image. Defaults to `true` — adds a subtle
   * `scale-[1.03]` on parent hover (matches blog-7). Set `false` for
   * static covers (skeletons, placeholders).
   */
  hoverZoom?: boolean;
  /**
   * Branded fallback rendered when `src` is missing or the image errors.
   * Domain-specific cards pass entity-shaped placeholders here (gradient +
   * initials for studios, type-icon for experiences, etc.) so consumers
   * never have to handle the empty cover case themselves.
   */
  fallback?: React.ReactNode;
  /**
   * When set, overlays a `<PixelCard>` canvas on top of the cover image and
   * dims the image on hover so absolutely-positioned overlay children (badges,
   * status pills, scroll strip) read more clearly. Pass `true` for the muted
   * default, or an object to tune `variant` / `gap` / `speed` / `colors` /
   * `noFocus`. Default `false`.
   */
  pixelEffect?: CardCoverPixelEffect;
}

export const CardCover = React.forwardRef<HTMLDivElement, CardCoverProps>(
  (
    {
      className,
      view,
      aspect,
      children,
      hoverZoom = true,
      src,
      alt = "",
      fallback,
      pixelEffect = false,
      ...rest
    },
    ref,
  ) => {
    const pixelEnabled = pixelEffect !== false;
    const pixelConfig: Pick<PixelCardProps, "variant" | "gap" | "speed" | "colors" | "noFocus"> =
      typeof pixelEffect === "object" && pixelEffect !== null
        ? pixelEffect
        : { variant: "muted", noFocus: true };

    const [hovered, setHovered] = React.useState(false);
    const handleEnter = React.useCallback(() => {
      if (pixelEnabled) setHovered(true);
    }, [pixelEnabled]);
    const handleLeave = React.useCallback(() => {
      if (pixelEnabled) setHovered(false);
    }, [pixelEnabled]);

    return (
      <div
        ref={ref}
        className={cn(cardCoverVariants({ view, aspect }), className)}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Image
          src={src}
          alt={alt}
          aspectRatio={
            aspect === "16/11"
              ? "16 / 11"
              : aspect === "4/3"
                ? "4 / 3"
                : aspect === "square"
                  ? "1 / 1"
                  : "16 / 9"
          }
          className={cn(
            "h-full w-full",
            hoverZoom &&
              "transition-transform duration-500 ease-smooth-out group-hover/card:scale-[1.03]",
            // When the pixel overlay is on, dip the image hard so the badges
            // that sit on top of it read clearly through the scatter of
            // pixels. Brightness drop pairs with the bottom shade so even
            // light covers get pulled down enough for chip contrast.
            pixelEnabled &&
              "transition-[opacity,filter,transform] duration-300 ease-smooth-out group-hover/card:opacity-50 group-hover/card:saturate-50 group-hover/card:brightness-75 motion-reduce:group-hover/card:opacity-70",
          )}
          errorContent={fallback}
          {...rest}
        />
        {/* Hover veil — subtle bottom-up gradient that fades in on parent hover.
            Adds depth + a "press-here" affordance without competing with overlay
            children. Reduced-motion users get a static (very faint) veil so the
            visual hierarchy still reads. */}
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 bg-gradient-to-t from-foreground/15 via-transparent to-transparent",
            "opacity-0 transition-opacity duration-300 ease-smooth-out",
            "group-hover/card:opacity-100 motion-reduce:transition-none motion-reduce:opacity-0",
          )}
        />
        {pixelEnabled ? (
          <PixelCard
            overlay
            active={hovered}
            variant={pixelConfig.variant ?? "muted"}
            gap={pixelConfig.gap}
            speed={pixelConfig.speed}
            colors={pixelConfig.colors}
            noFocus={pixelConfig.noFocus ?? true}
            aria-hidden
          />
        ) : null}
        {children ? <div className="absolute inset-0">{children}</div> : null}
      </div>
    );
  },
);
CardCover.displayName = "CardCover";

/* -------------------------------------------------------------------------- */
/* CardHeader / CardBody / CardFooter — layout slots                          */
/* -------------------------------------------------------------------------- */

const cardBodyVariants = cva("flex flex-col", {
  variants: {
    view: {
      // `flex-1` so the body absorbs remaining card height inside a
      // `flex flex-col` Card (grid view's outer Card). Combined with
      // `<CardFooter>`'s `mt-auto`, this keeps the action footer pinned
      // to the bottom across siblings with varied middle-row heights.
      grid: "flex-1 gap-3 px-0.5",
      list: "flex-1 min-w-0 gap-2 justify-center",
    },
  },
  defaultVariants: { view: "grid" },
});

export interface CardBodyProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardBodyVariants> {}

export const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, view, ...props }, ref) => (
    <div ref={ref} className={cn(cardBodyVariants({ view }), className)} {...props} />
  ),
);
CardBody.displayName = "CardBody";

export type CardHeaderProps = React.HTMLAttributes<HTMLDivElement>;
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center gap-2", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

export type CardFooterProps = React.HTMLAttributes<HTMLDivElement>;
/**
 * Footer slot for `<Card>`. `mt-auto` pins it to the bottom of a
 * `flex flex-col` parent (the default grid `<Card>`), so siblings in a
 * grid render their action rows at the same Y regardless of how their
 * body content distributes. Pair with `<CardBody view="grid">` (which
 * claims `flex-1`) for the uniform-footer behavior to land.
 *
 * Override `mt-auto` via `className="!mt-0"` if you want natural-position
 * footer behavior (rare).
 */
export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("mt-auto flex items-center gap-2 pt-2", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

/* -------------------------------------------------------------------------- */
/* CardTag — colored pill, matches blog-7's tag styling                       */
/* -------------------------------------------------------------------------- */

const cardTagVariants = cva(
  "inline-flex self-start items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "bg-muted text-muted-foreground",
        violet: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
        amber: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
        emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
        sky: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
        rose: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
        primary: "bg-primary/10 text-primary",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface CardTagProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof cardTagVariants> {}

export const CardTag = React.forwardRef<HTMLSpanElement, CardTagProps>(
  ({ className, tone, ...props }, ref) => (
    <span ref={ref} className={cn(cardTagVariants({ tone }), className)} {...props} />
  ),
);
CardTag.displayName = "CardTag";

/* -------------------------------------------------------------------------- */
/* CardTitle / CardDescription — typography slots with optional clamp         */
/* -------------------------------------------------------------------------- */

const clampClass = (n?: 1 | 2 | 3) =>
  n === 1 ? "line-clamp-1" : n === 2 ? "line-clamp-2" : n === 3 ? "line-clamp-3" : "";

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: "h2" | "h3" | "h4";
  /**
   * Visual size — independent of the heading tag. `lg` is the blog-7 hero
   * size used in grid cards; `md` is the row-style list-card size; `sm`
   * is for tight nested contexts.
   */
  size?: "sm" | "md" | "lg";
  clamp?: 1 | 2 | 3;
  /**
   * Fallback rendered when children is empty / nullish. Default `"Untitled"`
   * in muted color so a missing title never collapses the card layout.
   * Pass `null` to opt out.
   */
  emptyFallback?: React.ReactNode;
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  (
    {
      className,
      as = "h3",
      size = "md",
      clamp = 2,
      emptyFallback = "Untitled",
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = as as React.ElementType;
    const isEmpty =
      children === null ||
      children === undefined ||
      children === false ||
      (typeof children === "string" && children.trim().length === 0);
    return (
      <Comp
        ref={ref}
        className={cn(
          "font-semibold text-foreground leading-tight tracking-[-0.01em]",
          size === "lg" && "text-2xl sm:text-3xl",
          size === "md" && "text-lg sm:text-xl",
          size === "sm" && "text-base",
          clampClass(clamp),
          isEmpty && "text-muted-foreground italic",
          className,
        )}
        {...props}
      >
        {isEmpty ? emptyFallback : children}
      </Comp>
    );
  },
);
CardTitle.displayName = "CardTitle";

export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  clamp?: 1 | 2 | 3;
}

export const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, clamp = 2, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        "text-sm sm:text-base text-muted-foreground leading-relaxed",
        clampClass(clamp),
        className,
      )}
      {...props}
    />
  ),
);
CardDescription.displayName = "CardDescription";

/* -------------------------------------------------------------------------- */
/* CardMeta — dot-separated metadata strip                                    */
/* -------------------------------------------------------------------------- */

export interface CardMetaProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Each item renders as a span; consecutive items are separated by a dot. */
  items: Array<React.ReactNode | null | undefined>;
}

export const CardMeta = React.forwardRef<HTMLDivElement, CardMetaProps>(
  ({ className, items, ...props }, ref) => {
    const visible = items.filter(Boolean);
    if (visible.length === 0) return null;
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-muted-foreground",
          "min-w-0",
          className,
        )}
        {...props}
      >
        {visible.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 ? (
              <span
                className="w-1 h-1 rounded-full bg-muted-foreground/50 flex-shrink-0"
                aria-hidden
              />
            ) : null}
            <span className="inline-flex items-center gap-1 min-w-0 truncate">{item}</span>
          </React.Fragment>
        ))}
      </div>
    );
  },
);
CardMeta.displayName = "CardMeta";

/* -------------------------------------------------------------------------- */
/* CardStats — icon + numeric strip                                           */
/* -------------------------------------------------------------------------- */

export interface CardStat {
  icon?: React.ReactNode;
  value: React.ReactNode;
  label?: string;
  tooltip?: React.ReactNode;
}

export interface CardStatsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  stats: CardStat[];
}

export const CardStats = React.forwardRef<HTMLDivElement, CardStatsProps>(
  ({ className, stats, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground",
        className,
      )}
      {...props}
    >
      {stats.map((stat, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1"
          aria-label={stat.label}
          title={typeof stat.tooltip === "string" ? stat.tooltip : undefined}
        >
          {stat.icon ? (
            <span className="[&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground/70">
              {stat.icon}
            </span>
          ) : null}
          <span className="font-medium text-foreground">{stat.value}</span>
        </span>
      ))}
    </div>
  ),
);
CardStats.displayName = "CardStats";

/* -------------------------------------------------------------------------- */
/* CardActions — action button row                                            */
/* -------------------------------------------------------------------------- */

export interface CardActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Default `"end"` — actions sit on the right edge. `"between"` justifies
   * left/right; `"stretch"` makes children flex-grow equally (matches the
   * footer-button-pair pattern in current Studio Manager cards).
   */
  layout?: "end" | "between" | "stretch";
}

export const CardActions = React.forwardRef<HTMLDivElement, CardActionsProps>(
  ({ className, layout = "end", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 w-full",
        layout === "end" && "justify-end",
        layout === "between" && "justify-between",
        layout === "stretch" && "[&>*]:flex-1",
        className,
      )}
      {...props}
    />
  ),
);
CardActions.displayName = "CardActions";

/* -------------------------------------------------------------------------- */
/* CardSkeleton — full-card skeleton; mirrors grid + list layouts             */
/* -------------------------------------------------------------------------- */

export interface CardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  view?: "grid" | "list";
  /** Aspect ratio of the cover skeleton — defaults to "16/11" (matches grid). */
  aspect?: "16/11" | "16/9" | "4/3" | "square";
  /** Show a tag pill skeleton above the title. Default `true`. */
  hasTag?: boolean;
  /** Show a meta strip after the description. Default `true`. */
  hasMeta?: boolean;
  /** Show a footer action skeleton row. Default `true`. */
  hasFooter?: boolean;
  /**
   * Loading animation. `shimmer` (default) is the directional gradient sweep
   * that pairs with blog-7-style content cards; `pulse` is the classic
   * shadcn opacity fade. Both auto-degrade to static under reduced-motion.
   */
  animation?: "pulse" | "shimmer" | "none";
}

export const CardSkeleton = React.forwardRef<HTMLDivElement, CardSkeletonProps>(
  (
    {
      className,
      view = "grid",
      aspect = "16/11",
      hasTag = true,
      hasMeta = true,
      hasFooter = true,
      animation = "shimmer",
      ...props
    },
    ref,
  ) => {
    if (view === "list") {
      return (
        <div
          ref={ref}
          className={cn(
            CARD_NOTCH,
            "flex flex-row items-stretch gap-5 p-5 sm:p-6 bg-muted/40",
            className,
          )}
          {...props}
        >
          <Skeleton
            variant="card"
            animation={animation}
            className={cn("flex-shrink-0 w-32 sm:w-40 lg:w-56", aspectClass(aspect))}
          />
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
            {hasTag ? <Skeleton animation={animation} className="h-5 w-20 rounded-full" /> : null}
            <Skeleton animation={animation} className="h-5 w-3/4" />
            <Skeleton animation={animation} className="h-4 w-full" />
            <Skeleton animation={animation} className="h-4 w-2/3" />
            {hasMeta ? <Skeleton animation={animation} className="h-3 w-1/2 mt-1" /> : null}
          </div>
          {hasFooter ? (
            <Skeleton animation={animation} className="h-9 w-24 rounded-full self-center" />
          ) : null}
        </div>
      );
    }
    return (
      <div ref={ref} className={cn(CARD_NOTCH, "flex flex-col gap-5", className)} {...props}>
        <Skeleton
          variant="card"
          animation={animation}
          className={cn("w-full", aspectClass(aspect))}
        />
        <div className="flex flex-col gap-3 px-0.5">
          {hasTag ? <Skeleton animation={animation} className="h-5 w-20 rounded-full" /> : null}
          <Skeleton animation={animation} className="h-6 w-4/5" />
          <Skeleton animation={animation} className="h-4 w-full" />
          <Skeleton animation={animation} className="h-4 w-2/3" />
          {hasMeta ? <Skeleton animation={animation} className="h-3 w-1/2 mt-1" /> : null}
          {hasFooter ? (
            <div className="flex items-center gap-2 pt-2">
              <Skeleton animation={animation} className="h-9 flex-1 rounded-full" />
              <Skeleton animation={animation} className="h-9 flex-1 rounded-full" />
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);
CardSkeleton.displayName = "CardSkeleton";

const aspectClass = (a: "16/11" | "16/9" | "4/3" | "square") =>
  a === "16/11"
    ? "aspect-[16/11]"
    : a === "4/3"
      ? "aspect-[4/3]"
      : a === "square"
        ? "aspect-square"
        : "aspect-video";
